import { create } from "zustand";
import { getCookie, setCookie } from "cookies-next";
import {
  CONSENT_COOKIE_NAME,
  CONSENT_COOKIE_DOMAIN,
  CONSENT_COOKIE_MAX_AGE,
  ConsentCategory,
} from "./constants";
import {
  COUNTRY_COOKIE_NAME,
  isGdprCountry,
  isNoBannerCountry,
} from "./gdprRegions";
import { logConsentEvent } from "./observability";

export type ConsentState = Record<ConsentCategory, boolean>;

const DEFAULT_CONSENT: ConsentState = {
  [ConsentCategory.Necessary]: true,
  [ConsentCategory.Performance]: false,
  [ConsentCategory.Advertising]: false,
};

function parseConsentCookie(): ConsentState | null {
  const raw = getCookie(CONSENT_COOKIE_NAME);
  if (!raw || typeof raw !== "string") return null;

  try {
    const parsed = JSON.parse(raw);
    return {
      [ConsentCategory.Necessary]: true,
      [ConsentCategory.Performance]: !!parsed[ConsentCategory.Performance],
      [ConsentCategory.Advertising]: !!parsed[ConsentCategory.Advertising],
    };
  } catch {
    return null;
  }
}

function saveConsentCookie(state: ConsentState) {
  setCookie(CONSENT_COOKIE_NAME, JSON.stringify(state), {
    domain: CONSENT_COOKIE_DOMAIN,
    maxAge: CONSENT_COOKIE_MAX_AGE,
    path: "/",
    sameSite: "lax",
  });
}

function updateTrackingConsent(state: ConsentState) {
  if (typeof window === "undefined") return;

  const performance = state[ConsentCategory.Performance];
  const advertising = state[ConsentCategory.Advertising];

  // Google Analytics / Ads — Consent Mode v2
  if (window.gtag) {
    window.gtag("consent", "update", {
      analytics_storage: performance ? "granted" : "denied",
      ad_storage: advertising ? "granted" : "denied",
      ad_user_data: advertising ? "granted" : "denied",
      ad_personalization: advertising ? "granted" : "denied",
    });
  } else {
    logConsentEvent({ name: "consent_tracker_missing", tracker: "gtag" });
  }

  if (window.fbq) {
    window.fbq("consent", advertising ? "grant" : "revoke");
  } else if (advertising) {
    // 광고 consent 있는데 픽셀 미로드 = 누락 신호. denied는 무시(픽셀 자체가 안 뜬 정상 케이스).
    logConsentEvent({ name: "consent_tracker_missing", tracker: "fbq" });
  }

  if (window.clarity) {
    window.clarity("consent", performance);
  } else if (performance) {
    logConsentEvent({ name: "consent_tracker_missing", tracker: "clarity" });
  }
}

// GPC(Global Privacy Control) — 미국 8개 주(CCPA, Colorado 등)에서 법적 구속력.
// 브라우저가 유저의 "Do Not Sell/Share" 의사를 이 플래그로 전달.
function isGpcEnabled(): boolean {
  return (
    typeof navigator !== "undefined" && navigator.globalPrivacyControl === true
  );
}

interface ConsentStore {
  consent: ConsentState;
  hasConsented: boolean;
  /**
   * init()이 1회 실행돼 지역/쿠키 기반 consent 상태가 확정됐는지.
   * 초기값은 false이며, init()이 useEffect에서 돌기 때문에 직접 풀 페이지 로드
   * 직후에는 잠시 false다. consent 결정에 의존하는 발사(예: trackPurchase)는
   * hasConsented가 아니라 이 플래그가 true가 된 뒤 시도해야 마운트 레이스로 인한
   * 누락을 막는다. (hasConsented=false ≠ "미초기화"임에 주의 — GDPR 미동의도 false)
   */
  initialized: boolean;
  showBanner: boolean;
  showSettings: boolean;
  /**
   * "Accept Only Necessary" 버튼 노출 여부 = opt-in 요구 지역.
   * GDPR 지역(EU/UK/EEA/CH) 또는 GPC 시그널이 있으면 true.
   * 비-GDPR + GPC 없음 = false → Nike 패턴(비필수 기본 ON).
   */
  showDeclineButton: boolean;

  init: () => void;
  acceptAll: () => void;
  acceptNecessaryOnly: () => void;
  savePreferences: (state: ConsentState) => void;
  openSettings: () => void;
  closeSettings: () => void;
}

const ALL_ACCEPTED_CONSENT: ConsentState = {
  [ConsentCategory.Necessary]: true,
  [ConsentCategory.Performance]: true,
  [ConsentCategory.Advertising]: true,
};

function applyConsent(
  set: (state: Partial<ConsentStore>) => void,
  state: ConsentState,
) {
  saveConsentCookie(state);
  updateTrackingConsent(state);
  set({
    consent: state,
    hasConsented: true,
    // 동의를 확정하는 모든 경로(acceptAll/savePreferences 등)도 initialized를 세운다.
    // init()만 세우면 "배너가 init 후 렌더된다"는 호출 순서에 의존하게 되므로,
    // 여기서도 세워 "consent 확정 = initialized" 불변식을 무조건 성립시킨다.
    initialized: true,
    showBanner: false,
    showSettings: false,
  });
}

export const useConsentStore = create<ConsentStore>((set) => ({
  consent: DEFAULT_CONSENT,
  hasConsented: false,
  initialized: false,
  showBanner: false,
  showSettings: false,
  showDeclineButton: true,

  init: () => {
    const saved = parseConsentCookie();
    if (saved) {
      set({
        consent: saved,
        hasConsented: true,
        initialized: true,
        showBanner: false,
      });
      updateTrackingConsent(saved);
      const savedCountry = getCookie(COUNTRY_COOKIE_NAME) as string | undefined;
      logConsentEvent({
        name: "consent_init",
        source: "saved_cookie",
        country: savedCountry || undefined,
        isGdpr: isGdprCountry(savedCountry),
        gpc: isGpcEnabled(),
        noBanner: isNoBannerCountry(savedCountry),
        shouldOptOut: false,
        hasConsented: true,
      });
      return;
    }

    // 첫 방문: middleware가 내린 bhap_country 쿠키로 지역 판단
    const country = getCookie(COUNTRY_COOKIE_NAME) as string | undefined;
    const isGdpr = isGdprCountry(country);
    const gpc = isGpcEnabled();
    const noBanner = isNoBannerCountry(country);

    // 우선순위: GPC > GDPR > no-banner 지역 > 기본 Nike 패턴.
    // GPC는 지역 무관하게 유저의 명시적 opt-out 의사이므로 항상 최우선.
    const shouldOptOut = isGdpr || gpc;
    const initialConsent = shouldOptOut
      ? DEFAULT_CONSENT
      : ALL_ACCEPTED_CONSENT;

    // no-banner 지역(KR/JP/CN 등): 배너 생략, tracker 기본 ON.
    // Footer Cookie Settings 링크로 opt-out 경로 유지.
    // GPC가 있으면 shouldOptOut=true이므로 이 분기 도달 안 함 → GPC 자동 존중.
    const showBanner = shouldOptOut || !noBanner;

    set({
      consent: initialConsent,
      hasConsented: !shouldOptOut,
      initialized: true,
      showBanner,
      showDeclineButton: shouldOptOut,
    });

    if (!shouldOptOut) updateTrackingConsent(initialConsent);

    logConsentEvent({
      name: "consent_init",
      source: "fresh_visit",
      country: country || undefined,
      isGdpr,
      gpc,
      noBanner,
      shouldOptOut,
      hasConsented: !shouldOptOut,
    });
  },

  acceptAll: () => {
    applyConsent(set, ALL_ACCEPTED_CONSENT);
    logConsentEvent({
      name: "consent_changed",
      action: "accept_all",
      performance: true,
      advertising: true,
    });
  },

  acceptNecessaryOnly: () => {
    applyConsent(set, { ...DEFAULT_CONSENT });
    logConsentEvent({
      name: "consent_changed",
      action: "accept_necessary",
      performance: false,
      advertising: false,
    });
  },

  savePreferences: (state: ConsentState) => {
    const next = { ...state, [ConsentCategory.Necessary]: true };
    applyConsent(set, next);
    logConsentEvent({
      name: "consent_changed",
      action: "save_preferences",
      performance: next[ConsentCategory.Performance],
      advertising: next[ConsentCategory.Advertising],
    });
  },

  // Footer 등 Banner 외부에서 Cookies Settings 다이얼로그를 열 때 사용.
  // GDPR Article 7(3)(동의 철회 자유) 및 CCPA/CPRA "Do Not Sell" 요건 대응.
  openSettings: () => set({ showSettings: true }),
  closeSettings: () => set({ showSettings: false }),
}));
